/** End-of-day paper reconciliation: scan each returned paper, see which pile it goes in. */
import { useEffect, useRef, useState } from 'react';
import { api, ApiError } from '../shared/api.ts';
import { Link } from '../shared/router.tsx';
import { Card, Empty } from '../shared/ui.tsx';

interface Scan {
  at: string;
  paper: { id: string; civilosNumber: string; lastName: string; address: string };
  returnedFrom: string | null;
  instruction: { pile: string; title: string; detail: string[]; tone: 'ok' | 'info' | 'warn' | 'bad'; nextService?: { label: string } | null };
}

const KEY = 'civilos.reconcile.session';
const load = (): Scan[] => {
  try {
    return JSON.parse(localStorage.getItem(KEY) ?? '[]');
  } catch {
    return [];
  }
};
const save = (s: Scan[]) => {
  try {
    localStorage.setItem(KEY, JSON.stringify(s));
  } catch {
    /* private window: the session just isn't remembered across reloads */
  }
};

export function Reconcile() {
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [session, setSession] = useState<Scan[]>(load);
  const input = useRef<HTMLInputElement>(null);
  const last = session[0] ?? null;
  useEffect(() => save(session), [session]);

  const scan = async (raw: string) => {
    const n = raw.trim();
    if (!n) return;
    setBusy(true);
    setErr(null);
    try {
      const r = await api.post<Omit<Scan, 'at'>>('/api/reconcile/scan', { paperNumber: n });
      setSession((s) => [{ ...r, at: new Date().toISOString() }, ...s.filter((x) => x.paper.id !== r.paper.id)]);
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Could not reach the server.');
    } finally {
      setBusy(false);
      setCode('');
      input.current?.focus();
    }
  };

  const piles = new Map<string, Scan[]>();
  for (const s of session) piles.set(s.instruction.pile, [...(piles.get(s.instruction.pile) ?? []), s]);

  return (
    <div className="stack rec">
      <div className="row between wrap gap no-print">
        <div>
          <h1>Paper reconciliation</h1>
          <p className="muted">Scan each paper as it comes back. CivilHound checks it into the office and tells you which pile it goes in.</p>
        </div>
      </div>
      <form
        className="rec-scan no-print"
        onSubmit={(e) => {
          e.preventDefault();
          scan(code);
        }}
      >
        <input ref={input} aria-label="Scan paper barcode" placeholder="Scan barcode or type paper number" value={code} onChange={(e) => setCode(e.target.value)} autoFocus disabled={busy} />
        <button className="btn primary" disabled={busy || !code.trim()}>
          {busy ? 'Checking…' : 'Check'}
        </button>
      </form>
      {err && (
        <div className="rec-result bad no-print" role="alert">
          <div className="rec-pile">Not found</div>
          <div className="rec-title">{err}</div>
        </div>
      )}
      {last && !err && (
        <div className={`rec-result ${last.instruction.tone} no-print`} role="status" aria-live="polite">
          <div className="rec-top">
            <span className="mono rec-num">{last.paper.civilosNumber}</span>
            <span className="rec-name">{last.paper.lastName}</span>
            <span className="muted">{last.paper.address}</span>
            {last.returnedFrom && <span className="rec-chip">Checked in from {last.returnedFrom}</span>}
          </div>
          <div className="rec-pile">{last.instruction.pile}</div>
          <div className="rec-title">{last.instruction.title}</div>
          {last.instruction.detail.map((d, i) => (
            <div key={i} className="rec-detail">
              {d}
            </div>
          ))}
          <Link className="btn sm" to={`/papers/${last.paper.id}`}>
            Open paper
          </Link>
        </div>
      )}

      <Card
        title={`Sort sheet — ${session.length} paper${session.length === 1 ? '' : 's'}`}
        actions={
          session.length > 0 && (
            <span className="row gap no-print">
              <button className="btn sm" onClick={() => window.print()}>
                Print sort sheet
              </button>
              <button className="btn sm ghost" onClick={() => window.confirm('Start a new session? The list clears; the papers are already updated.') && setSession([])}>
                New session
              </button>
            </span>
          )
        }
      >
        {session.length === 0 && <Empty>Scanned papers will be grouped here by pile.</Empty>}
        <div className="rec-piles">
          {[...piles].map(([pile, list]) => (
            <section key={pile} className={`rec-group ${list[0]!.instruction.tone}`}>
              <h3>
                {pile} <span className="tab-count">{list.length}</span>
              </h3>
              <ul>
                {list.map((s) => (
                  <li key={s.paper.id}>
                    <span className="mono">{s.paper.civilosNumber}</span> <strong>{s.paper.lastName}</strong> <span className="muted small">{s.instruction.title}</span>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>
      </Card>
    </div>
  );
}
